import {
  CustomEditor,
  DomEventData,
  DowncastWriter,
  EditorConfig,
  UpcastWriter
} from 'ckeditor5-extra-build'
import {MutableRefObject, useCallback, useEffect, useMemo, useRef} from 'react'
import {Lang} from 'yougile-lang'
import {HTMLUtil} from 'yougile-util'
import {DataObjectId, Hub, MessageTextChunk} from 'yougile-model'
import {Backend} from '@common/backend'
import {
  initMentionConversions,
  mentionElemRegEx,
  useCKEditorCustomMentions
} from '@components/common/ckeditor-custom-mentions'
import {ChatInputStore} from '@components/right-panel/chat/chat-input/chat-input-store'
import {ChatStore} from '@components/right-panel/chat/chat-store'
import {useSyncEventUpdateEffect} from '@containers/hooks'
import {EmojiUtils} from '@extensions/emoji-utils'
import {globals} from '@extensions/globals-base'
import {MessageUtils} from '@extensions/message-utils'
import {extractAllFiles} from '../chat-messages/message/utils/extract-files'
import {handleEditCanceling, handleReplyCanceling} from '../utils'

export type CustomEditorRef = MutableRefObject<CustomEditor | undefined>

const chatEditorConfig = {
  toolbar: {
    items: [
      'bold',
      'italic',
      'strikethrough',
      'underline',
      '|',
      'link',
      '|',
      'bulletedList',
      'numberedList'
    ],
    shouldNotGroupWhenFull: true
  },
  language: 'ru',
  link: {
    addTargetToExternalLinks: true,
    // prefix links without protocol with `http://`
    defaultProtocol: 'http://'
  }
} as EditorConfig

const useEditorConfig = (hub?: Hub, placeholder?: string): EditorConfig => {
  const mentionsConfig = useCKEditorCustomMentions(
    'chat-input-mention-list-item',
    hub
  )
  return useMemo(
    (): EditorConfig => ({
      ...chatEditorConfig,
      ...mentionsConfig,
      ...{
        placeholder: placeholder || Lang.msg.writeNewMessage
      }
    }),
    [chatEditorConfig, mentionsConfig, Lang.msg, placeholder]
  )
}

const cursorToEnd = (editor: CustomEditor): void => {
  editor.model.change((writer: DowncastWriter) => {
    try {
      const root = editor.model.document.getRoot()
      const child = root?.getChild(root?.childCount - 1)
      child && writer.setSelection(writer.createPositionAt(child, 'end'))
    } catch (e) {
      // can be skipped
    }
  })
}

const useOnEdit = (
  editorRef: CustomEditorRef,
  chatStore: ChatStore,
  chatInputStore: ChatInputStore
): void =>
  useEffect(() => {
    const message = chatStore.state.messageToEdit
    if (!message) {
      chatInputStore.setMessageFiles([])
      return
    }

    const messageFiles = extractAllFiles(message)
    chatInputStore.setMessageFiles(messageFiles)

    const text = chatInputStore.state.messageHtml
    const editor = editorRef.current
    if (editor) {
      editor.setData(text)
      editor.focus()
      cursorToEnd(editor)
      editor.editing.view.scrollToTheSelection()
    }
  }, [editorRef, chatStore.state.messageToEdit])

const useOnReply = (editorRef: CustomEditorRef, chatStore: ChatStore): void =>
  useEffect(() => {
    chatStore.state.messageToReply && editorRef.current?.focus()
  }, [chatStore.state.messageToReply])

const replaceMentions = (
  text: string
): {text: string; chunks: MessageTextChunk[]} => {
  const chunks: MessageTextChunk[] = []
  text = text.replace(
    new RegExp(mentionElemRegEx, 'gi'),
    (_: string, userId: DataObjectId, replacement: string) => {
      chunks.push({type: 'user', replacement, data: {userId}})
      return replacement
    }
  )
  return {text, chunks}
}

export const emojiRegex = /<twemoji[^>]*alt="([^"]+)"[^>]*><\/twemoji>/

const replaceEmoji = (text: string): string =>
  text.replace(new RegExp(emojiRegex, 'gi'), (_, alt) => alt)

const aElemRegEx = /<a[^>]+href="([^"]+)"[^>]*>([^<]+)<\/a>/gi

// todo: try replacing with downcasting (requires overriding downcasts in `ckeditor5-link`)
const unnestTaskLinks = (text: string): string =>
  text.replace(aElemRegEx, (aElem: string, href: string, content: string) => {
    if (href.match(MessageUtils.rxTaskLink) && href === content) {
      return href
    }
    return aElem
  })

const useSendMessage = (
  editorRef: CustomEditorRef,
  chatInputStore: ChatInputStore,
  chatStore: ChatStore
): (() => void) =>
  useCallback(() => {
    let text = editorRef.current?.getData() ?? ''
    text = replaceEmoji(text)
    text = unnestTaskLinks(text)
    const mentionsRes = replaceMentions(text)
    const chunks = mentionsRes.chunks

    const repMsg = chatStore.state.messageToReply
    if (repMsg && repMsg.from !== globals.currentUserId) {
      chunks.push({
        type: 'user',
        replacement: 'test123',
        data: {userId: repMsg.from}
      })
    }

    text = mentionsRes.text
    text = HTMLUtil.sanitizeHtml(text)
    text = HTMLUtil.trimEmptyParagraphs(text)
    editorRef.current?.setData('')
    editorRef.current?.focus()

    void chatInputStore.onEditorSubmit(text, {chunks}, editorRef)
  }, [editorRef, chatInputStore])

const useOnEmojiSelect = (editorRef: CustomEditorRef): ((s: string) => void) =>
  useCallback(
    (symbol: string) => {
      const editor = editorRef.current
      if (!editor) {
        return
      }

      editor.model.change((writer: DowncastWriter) => {
        const parsedSymbol = EmojiUtils.parse(symbol)
        if (parsedSymbol !== symbol) {
          // not native emoji, use custom editor element
          // see `initEmojiConversion` for `twemoji` model definition and casts
          const el = document.createElement('div')
          el.innerHTML = parsedSymbol

          const img = el.querySelector('img')
          if (img) {
            editor.model.insertContent(
              writer.createElement('twemoji', {alt: img.alt, src: img.src}),
              editor.model.document.selection
            )
          }
        } else {
          // native emoji, place as text
          editor.model.insertContent(
            writer.createText(symbol),
            editor.model.document.selection
          )
        }
        editor.model.insertContent(
          writer.createText(' '),
          editor.model.document.selection
        )
      })
      editor?.focus()
    },
    [editorRef]
  )

const addCustomKeystrokes = (
  editor: CustomEditor,
  sendMessage: () => void,
  chatStore: ChatStore,
  chatInputStore: ChatInputStore
): void => {
  // edit previous message
  editor.keystrokes.set(
    'arrowup',
    (_: KeyboardEvent, onConfirmed?: () => void) => {
      const text = editor.getData()
      if (
        !text &&
        chatInputStore.state.hub &&
        chatInputStore.state.hub.chat.getMessagesCount() > 0
      ) {
        const lastMsg = chatInputStore.state.hub.chat.lastMessage({
          userId: globals.currentUserId
        })
        if (!lastMsg) {
          return
        }
        chatStore.startEdit(lastMsg)
        onConfirmed?.()
      }
    },
    {priority: 'highest'}
  )

  // if editing or replying cancel it
  editor.keystrokes.set(
    'esc',
    async (e: Event, onConfirmed?: () => void) => {
      if (chatStore.state.messageToEdit) {
        e.stopPropagation()
        e.preventDefault()

        await handleEditCanceling({
          editor,
          chatStore,
          chatInputStore,
          onConfirmed
        })
      }
      if (chatStore.state.messageToReply) {
        e.stopPropagation()
        e.preventDefault()

        await handleReplyCanceling({
          editor,
          chatStore,
          chatInputStore,
          onConfirmed
        })
      }
    },
    {priority: 'highest'}
  )

  // send message on enter
  editor.keystrokes.set(
    'enter',
    (_: KeyboardEvent, cancel: () => void) => {
      cancel?.()
      sendMessage()
    },
    {priority: 'highest'}
  )

  // insert line break on ctrl+enter/shift+enter
  const onEnter = (originalEvent: KeyboardEvent): void => {
    const view = editor.editing.view
    const event = new KeyboardEvent(originalEvent.type, {
      ...originalEvent,
      shiftKey: false,
      ctrlKey: false
    })
    // fire internal 'enter' event as if Enter was pressed without modifier keys
    // `isSoft: false` is for ckeditor5-enter plugin to register a hard linebreak
    // firing event required so that plugin chain (ckeditor5-list, ckeditor5-enter) works correctly
    view.document.fire(
      'enter',
      new DomEventData(view.document, event, {isSoft: false})
    )
    view.scrollToTheSelection()
  }

  for (const keystroke of ['ctrl+enter', 'shift+enter']) {
    editor.keystrokes.set(
      keystroke,
      (evt: {domEvent: KeyboardEvent}, cancel: () => void) => {
        cancel?.()
        onEnter(evt.domEvent)
      },
      {priority: 'highest'}
    )
  }
}

const initFilePaste = (
  editor: CustomEditor,
  chatInputStore: ChatInputStore
): void =>
  editor.listenTo(
    editor.editing.view.document,
    'paste',
    (
      _event: KeyboardEvent,
      data: {
        dataTransfer: {files: File[]}
        domEvent: ClipboardEvent
        preventDefault: () => void
        stopPropagation: () => void
      }
    ) => {
      if (data.dataTransfer.files?.length) {
        void chatInputStore.onEditorPaste(data.domEvent)
        data.preventDefault()
        data.stopPropagation()
      }
    },
    {priority: 'highest'}
  )

// must convert the same way as `UIUtil.highlightLinksInText`
const initLinkDowncastConversion = (editor: CustomEditor): void => {
  // 'link' for simple anchors, `linkHref` for `ckeditor5-link` plugin ones
  for (const model of ['link', 'linkHref']) {
    editor.conversion.for('dataDowncast').attributeToElement({
      model,
      view: (href: string, {writer}: {writer: DowncastWriter}) =>
        writer.createAttributeElement('a', {
          href,
          class: '_link',
          rel: 'nofollow',
          target: '_blank'
        }),
      converterPriority: 'highest'
    })
  }
}

const initMessageImageConversion = (editor: CustomEditor): void => {
  const model = 'messageImage'

  if (!editor.model.schema.isRegistered(model)) {
    editor.model.schema.register(model, {
      allowWhere: '$text',
      isObject: true,
      isInline: true
    })
  }

  editor.conversion.for('upcast').elementToElement({
    view: 'message-image',
    model: (view: HTMLElement, {writer}: {writer: UpcastWriter}) => {
      return writer.createElement(model, {
        original: view.getAttribute('data-original') ?? '',
        preview: view.getAttribute('data-preview') ?? '',
        previewWidth: +(view.getAttribute('data-preview-width') ?? 0),
        previewHeight: +(view.getAttribute('data-preview-height') ?? 0)
      })
    },
    converterPriority: 'highest'
  })

  editor.conversion.for('editingDowncast').elementToElement({
    model,
    view: (data: HTMLElement, {writer}: {writer: DowncastWriter}) => {
      const previewWidth = Number(data.getAttribute('previewWidth') ?? 0)
      const previewHeight = Number(data.getAttribute('previewHeight') ?? 0)
      const style =
        previewWidth > 0 && previewHeight > 0
          ? `width:${previewWidth}px;height:${previewHeight}px;`
          : ''
      return writer.createEmptyElement('img', {
        className: 'file-link-prev',
        src: Backend.toDataUrl(String(data.getAttribute('preview') ?? '')),
        style
      })
    },
    converterPriority: 'highest'
  })

  editor.conversion.for('dataDowncast').elementToElement({
    model,
    view: (data: HTMLElement, {writer}: {writer: DowncastWriter}) => {
      return writer.createText(
        '/root/#file:' + (data.getAttribute('preview') ?? '')
      )
    },
    converterPriority: 'highest'
  })
}

const initMessageDocumentConversion = (editor: CustomEditor): void => {
  const model = 'messageDocument'

  if (!editor.model.schema.isRegistered(model)) {
    editor.model.schema.register(model, {
      allowWhere: '$text',
      isObject: true,
      isInline: true
    })
  }

  editor.conversion.for('upcast').elementToElement({
    view: 'message-document',
    model: (view: HTMLElement, {writer}: {writer: UpcastWriter}) => {
      return writer.createElement(model, {
        link: view.getAttribute('data-link') ?? '',
        filename: view.getAttribute('data-filename') ?? ''
      })
    },
    converterPriority: 'highest'
  })

  editor.conversion.for('editingDowncast').elementToElement({
    model,
    view: (data: HTMLElement, {writer}: {writer: DowncastWriter}) => {
      // empty link is a hack to prevent editing of link's contents
      // more thorough solution is to create a widget, but it requires
      // writing a custom plugin
      return writer.createEmptyElement('a', {
        class: 'file-link fi-page',
        target: '_blank',
        tabIndex: '-1',
        href: data.getAttribute('link') ?? '',
        'data-content': data.getAttribute('filename') ?? ''
      })
    }
  })

  editor.conversion.for('dataDowncast').elementToElement({
    model,
    view: (data: HTMLElement, {writer}: {writer: DowncastWriter}) => {
      return writer.createText('/root/#file:' + data.getAttribute('link'))
    },
    converterPriority: 'highest'
  })
}

/**
 * `twemoji` model, used when native emojis are not available.
 * See `useOnEmojiSelect`
 */
const initEmojiConversion = (editor: CustomEditor): void => {
  const model = 'twemoji'
  const emojiClass = 'emoji'

  if (!editor.model.schema.isRegistered(model)) {
    editor.model.schema.register(model, {
      allowWhere: '$text',
      isObject: true,
      isInline: true,
      allowAttributes: ['alt', 'src']
    })
  }

  editor.conversion.for('upcast').elementToElement({
    view: model,
    model: (view: HTMLElement, {writer}: {writer: UpcastWriter}) => {
      return writer.createElement(model, {
        alt: view.getAttribute('data-alt') ?? '',
        src: view.getAttribute('data-src') ?? ''
      })
    },
    converterPriority: 'highest'
  })

  editor.conversion.for('editingDowncast').elementToElement({
    model,
    view: (data: HTMLElement, {writer}: {writer: DowncastWriter}) => {
      return writer.createEmptyElement('img', {
        class: emojiClass,
        alt: data.getAttribute('alt'),
        src: data.getAttribute('src')
      })
    },
    converterPriority: 'highest'
  })

  editor.conversion.for('dataDowncast').elementToElement({
    model,
    view: (data: HTMLElement, {writer}: {writer: DowncastWriter}) => {
      return writer.createEmptyElement(model, {
        'data-alt': data.getAttribute('alt'),
        'data-src': data.getAttribute('src')
      })
    },
    converterPriority: 'highest'
  })
}

const initScrollToInput = (editor: CustomEditor): void => {
  editor.editing.view.document.on('keydown', () => {
    editor.editing.view.scrollToTheSelection()
  })
}

const resetEditorData = (
  editor: CustomEditor,
  chatInputStore: ChatInputStore
): void => {
  editor.setData(chatInputStore.state.messageHtml)
  chatInputStore.state.autofocus && editor.focus()
  cursorToEnd(editor)
}

export const useChatInputCustomEditor = (
  chatStore: ChatStore,
  chatInputStore: ChatInputStore,
  hub?: Hub
): {
  editorConfig: EditorConfig
  sendMessage: () => void
  updateEditorRef: (editor: CustomEditor) => void
  editorRef: MutableRefObject<CustomEditor | undefined>
  onEmojiSelect: (s: string) => void
} => {
  // sync language change
  useSyncEventUpdateEffect('profile', {
    debounceTime: 50,
    filter: globals.currentUserId
  })
  const editorRef = useRef<CustomEditor>()
  const editorConfig = useEditorConfig(hub, chatInputStore.state.placeholder)

  useOnEdit(editorRef, chatStore, chatInputStore)
  useOnReply(editorRef, chatStore)

  const sendMessage = useSendMessage(editorRef, chatInputStore, chatStore)
  const onEmojiSelect = useOnEmojiSelect(editorRef)
  const updateEditorRef = useCallback(
    (editor: CustomEditor) => {
      editorRef.current = editor

      initMentionConversions(editor)

      addCustomKeystrokes(editor, sendMessage, chatStore, chatInputStore)
      initFilePaste(editor, chatInputStore)
      initLinkDowncastConversion(editor)
      initMessageImageConversion(editor)
      initMessageDocumentConversion(editor)
      initEmojiConversion(editor)
      initScrollToInput(editor)
      chatInputStore.loadChatState()
      resetEditorData(editor, chatInputStore)
    },
    [editorRef, sendMessage, chatStore, chatInputStore]
  )

  return {editorConfig, sendMessage, onEmojiSelect, updateEditorRef, editorRef}
}
